import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, TextField, InputAdornment, MenuItem, Select, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import AppShell from '../../../components/layout/AppShell'
import PageHeader from '../../../components/ui/PageHeader'

Index.layout = (page) => <AppShell>{page}</AppShell>

const TYPE_LABELS = { Person: 'Person', Company: 'Unternehmen' }

export default function Index({ contacts, filters = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [q, setQ] = useState(filters.q || '')
  const [type, setType] = useState(filters.type || '')
  const [active, setActive] = useState(filters.active || '')

  const basePath = `/m/${tenant.slug}/contacts`

  const applyFilters = () => {
    router.get(basePath, {
      q: q || undefined,
      type: type || undefined,
      active: active || undefined,
    }, { preserveState: true, replace: true })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') applyFilters()
  }

  return (
    <Box>
      <PageHeader
        title="Kontakte"
        breadcrumbs={[{ label: 'Dashboard', href: `/m/${tenant.slug}` }, { label: 'Kontakte' }]}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => router.visit(`${basePath}/new`)}
            data-testid="new-contact-btn"
          >
            Neuer Kontakt
          </Button>
        }
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Suche nach Name, E-Mail..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          sx={{ minWidth: 240 }}
          data-testid="search-input"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Typ</InputLabel>
          <Select
            value={type}
            label="Typ"
            onChange={(e) => setType(e.target.value)}
            data-testid="type-filter"
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="Person">Person</MenuItem>
            <MenuItem value="Company">Unternehmen</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={active}
            label="Status"
            onChange={(e) => setActive(e.target.value)}
            data-testid="active-filter"
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="true">Aktiv</MenuItem>
            <MenuItem value="false">Inaktiv</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={applyFilters} data-testid="filter-btn">
          Filtern
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>E-Mail</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Telefon</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Adresse</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Kontakte gefunden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {contacts.map((contact) => (
              <TableRow
                key={contact.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => router.visit(`${basePath}/${contact.id}`)}
                data-testid={`contact-row-${contact.id}`}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{contact.display_name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={TYPE_LABELS[contact.type] || contact.type}
                    color={contact.type === 'Person' ? 'primary' : 'secondary'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </TableCell>
                <TableCell><Typography variant="body2">{contact.email || '–'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{contact.phone || '–'}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {contact.primary_address_one_line || '–'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={contact.active ? 'Aktiv' : 'Inaktiv'}
                    color={contact.active ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
